import { Component, Input } from '@angular/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, Subject, combineLatest, map, startWith, switchMap, take, tap, withLatestFrom, } from 'rxjs';
import { IBaseGetByComanyRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CabinetService } from '../services/cabinet.service';
import { ICompany } from 'src/app/company/models/company';
import { ICabinet } from '../models/cabinet';
import { CabinetStore } from 'src/app/stores/component/cabinet-store';

@UntilDestroy()
@Component({
    selector: 'app-cabinet-view-admin',
    templateUrl: './cabinet-view-admin.component.html',
    styleUrl: './cabinet-view-admin.component.scss',
    providers: [CabinetStore],
    standalone: false
})
export class CabinetViewAdminComponent {

  ////////////////
  // variables //
  //////////////

  @Input() reloadResults : boolean = true;
  @Input() company: ICompany;

  vm$: Observable<any>;
  keyword : string;
  showReset = false;
  loading: boolean = false;

  constructor(
    private cabinetService: CabinetService,
  ) {
    this.vm$ = combineLatest([ 
      this.gridState$.pipe(),
      this.searchRequest$.pipe(
        switchMap((request) => {
          this.loading = true;
          return this.cabinetService.getCabinetsByCompany(request);
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(x => {
          var gridData : GridDataResult = {
            data: x.data,
            total: x.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      )
    ]).pipe(
      map(([
        gridState,
        cabinets,
        searchRequest,
        pageChangeSub
      ]) => ({
        gridState,
        cabinets,
        searchRequest,
        pageChangeSub
      }))
    )
  }

  cabinets: IGridResult<ICabinet[]>;

  ngOnInit(): void {
    this.searchRequest$.next({
      ...this.searchRequest$.value,
      companyId: this.company.id,
    });
  }

  /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }


  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseGetByComanyRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

}

function checkProperties(obj: Record<string, any>): boolean {
  return Object.values(obj).every(value => value === null);
}